import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

type Props = {
  anchorRect: DOMRect;
  onClose: () => void;
  onPickBullion: () => void;
  onPickSquares: () => void;
  onPickLeagues: () => void;
  showBullion?: boolean;
  showSquares?: boolean;
  showLeagues?: boolean;
};

/** Above ProfileModalShell (z-10050). */
const POPOVER_Z = 10060;

/**
 * “SELECT GAME” menu for CHALLENGE — BULLION / Squares / Leagues (fixed under trigger).
 * Games without playable matches (or create access for Leagues) are omitted entirely.
 */
export default function ChallengeGameSelectPopover({
  anchorRect,
  onClose,
  onPickBullion,
  onPickSquares,
  onPickLeagues,
  showBullion = true,
  showSquares = true,
  showLeagues = false,
}: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const hasAny = showBullion || showSquares || showLeagues;

  useEffect(() => {
    if (!hasAny) onClose();
  }, [hasAny, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const el = rootRef.current;
      const t = e.target;
      if (!el || !(t instanceof Node)) return;
      if (!el.contains(t)) onClose();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
    };
  }, [onClose]);

  if (!hasAny) return null;

  const width = 228;
  const left = Math.min(
    Math.max(8, anchorRect.right - width),
    typeof window !== "undefined" ? window.innerWidth - width - 8 : anchorRect.left
  );
  const top = anchorRect.bottom + 6;

  const menu = (
    <div
      ref={rootRef}
      className="fixed border-2 border-[#c99cff] bg-[#7a3eb8] p-2 shadow-[0_10px_28px_rgba(8,2,18,0.55)]"
      style={{ zIndex: POPOVER_Z, top, left, width }}
      role="dialog"
      aria-label="Select game"
    >
      <p className="mb-2 pl-0.5 font-anton text-[11px] font-bold uppercase leading-none tracking-wide text-white">
        Select game
      </p>
      <div className="flex flex-col gap-1.5">
        {showLeagues ? (
          <button
            type="button"
            onClick={() => {
              onPickLeagues();
              onClose();
            }}
            className="flex w-full items-center gap-2 bg-[#552ca6] px-2 py-2 text-left font-anton text-[14px] font-bold uppercase leading-none tracking-wide text-white transition-colors hover:bg-[#6138b0]"
          >
            <img
              src="/assets/league-trophy.png"
              alt=""
              className="h-9 w-9 shrink-0 object-contain"
              width={36}
              height={36}
            />
            LEAGUES
          </button>
        ) : null}
        {showBullion ? (
          <button
            type="button"
            onClick={() => {
              onPickBullion();
              onClose();
            }}
            className="flex w-full items-center gap-2 bg-[#552ca6] px-2 py-2 text-left font-anton text-[14px] font-bold uppercase leading-none tracking-wide text-white transition-colors hover:bg-[#6138b0]"
          >
            <img src="/assets/bullion.png" alt="" className="h-9 w-9 shrink-0 object-contain" width={36} height={36} />
            BULLION
          </button>
        ) : null}
        {showSquares ? (
          <button
            type="button"
            onClick={() => {
              onPickSquares();
              onClose();
            }}
            className="flex w-full items-center gap-2 bg-[#552ca6] px-2 py-2 text-left font-anton text-[14px] font-bold uppercase leading-none tracking-wide text-white transition-colors hover:bg-[#6138b0]"
          >
            <img src="/assets/squares.png" alt="" className="h-9 w-9 shrink-0 object-contain" width={36} height={36} />
            SQUARES
          </button>
        ) : null}
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(menu, document.body);
}
